import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useAnchoredPortalPanel from '../../hooks/useAnchoredPortalPanel';
import { useIsMobileOrderViewport } from '../../hooks/useIsMobileOrderViewport';

/**
 * THE free-text input with a suggestion list — a combobox: type anything, or
 * open the list with ▾ / ArrowDown and pick. Built 2026-10-19 for the gag
 * Unit and Primary effect fields (row, panel, New gag), replacing `<datalist>`.
 *
 * 🔑 **WHY NOT `<datalist>`.** Owner, 2026-10-18/19: *"no drop down list when I
 * click the down arrow … No options until I start typing"*. Every datalist
 * already had its options (each surface fetched on mount), so the fetch was
 * not the cause. The likely cause — Chrome's datalist popup lists only the
 * options matching the box's CURRENT text, so a filled box opens near-empty —
 * is REASONED, NOT OBSERVED: the native popup is browser UI outside the DOM
 * and could not be captured from an automated session (2026-10-19). Either
 * way the native popup cannot be told "show everything". Hence our own list:
 * ▾ (or ArrowDown on a closed list) shows EVERY option whatever the box
 * holds; typing narrows to case-insensitive substring matches.
 *
 * Contract:
 *  - FREE TEXT. The value is whatever is typed; an option is a shortcut, never
 *    a constraint. `onChange(string)` gets a STRING, never an event.
 *  - Picking an option equal to the current value calls nothing (no dirty).
 *  - It fetches nothing: callers pass `options` (one fetch per surface).
 *  - Combobox ARIA: the INPUT carries `role="combobox"`, `aria-expanded`,
 *    `aria-controls`, `aria-activedescendant` — and `aria-haspopup="listbox"`,
 *    which is LOAD-BEARING: the Breakdown row's Enter-saves / Escape-prompts
 *    handler yields to any `[aria-haspopup][aria-expanded="true"]` inside the
 *    row (gags/CLAUDE.md). Keys are therefore NOT stopped from propagating;
 *    while the list is open Enter and Escape are `preventDefault`ed instead, so
 *    a form does not submit and a window-level Escape listener can tell
 *    (`GagCreateModal` checks `defaultPrevented`).
 *  - Keys: ArrowDown/Up move (ArrowDown opens a closed list, all options);
 *    Enter picks the highlighted option or just closes; Escape closes and
 *    keeps the text; Tab/blur closes.
 *  - From `md` the list PORTALS to `<body>` (`hooks/useAnchoredPortalPanel`):
 *    the Breakdown's `TD` clips (`overflow-hidden`), and absolute positioning
 *    escapes an ancestor's LAYOUT, never its CLIP. Below `md` it stays in flow
 *    (absolute under the box): the portal path closes on scroll/resize, and a
 *    phone's keyboard opening is both.
 *
 * 🔀 FOUR OPTIONAL PROPS (2026-10-28, the order form's Job / Rig / Gag box,
 * job cost codes) — each defaults to the behaviour above, so every older
 * caller renders exactly as before:
 *  - `onPick(option)` — a PICK (click / Enter on a highlighted option) calls
 *    this INSTEAD of `onChange`, so a caller can tell "chose this option"
 *    from "typed the same text" (the order form links a gag only on a pick,
 *    never on a typed name). Called even when the option equals the value.
 *  - `optionGroup(option)` → a heading string: a non-option heading row
 *    (`role="presentation"`) is drawn wherever it changes. The caller orders
 *    `options` by group.
 *  - `openOnFocus` — focusing the box opens the full list.
 *  - `id` — a stable input id (the list is `${id}-list`).
 */

/** The one-line refusal under a box (`accept` here; the timesheet cell editor reuses it). */
export const INPUT_PROBLEM_CLASS =
  'absolute left-0 top-full z-20 mt-0.5 whitespace-nowrap rounded bg-white px-1 text-xs font-medium text-red-700 shadow dark:bg-gray-800 dark:text-red-300';

/** Case-insensitive substring filter; a null/blank query keeps every option. */
export function matchSuggestions(options, query) {
  const list = Array.isArray(options) ? options.filter((o) => typeof o === 'string' && o !== '') : [];
  const q = query == null ? '' : String(query).trim().toLowerCase();
  if (q === '') return list;
  return list.filter((o) => o.toLowerCase().includes(q));
}

const LIST_CLASS =
  'max-h-60 overflow-y-auto py-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl ' +
  'border border-gray-200 dark:border-gray-700';
// 📱 Below `md` (2026-10-28 phone audit): 15px text and ≥40px rows. `md:text-sm` is load-bearing —
// it exempts the option from index.css's `[data-mobile-compact] .text-sm:not([class*="md:text-"])`
// shrink to 12px, and keeps md+ exactly as before.
const OPTION_BASE = 'flex items-center gap-2 px-3 py-1.5 max-md:text-[15px] max-md:min-h-[40px] md:text-sm cursor-pointer text-gray-800 dark:text-gray-200';
const OPTION_ACTIVE = 'bg-blue-50 dark:bg-blue-900/40';
const TOGGLE_CLASS =
  'absolute inset-y-0 right-0 flex items-center justify-center w-7 rounded-r-md ' +
  'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 ' +
  'hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

export default function SuggestInput({
  value,
  onChange,
  options = [],
  inputClassName = '',
  ariaLabel,
  listLabel,
  testId,
  disabled = false,
  placeholder,
  // OPT-IN (2026-10-19, owner "ONE ENTER SAVES"): Enter on an open list with
  // no option highlighted closes it WITHOUT claiming the key, so a parent's
  // Enter handler still runs. Only the Breakdown's editing row passes it; the
  // panel (no Enter handler) and New gag (a <form>) keep Enter-just-closes, so
  // an open list never newly submits the modal.
  enterPassesThrough = false,
  onPick = null,
  optionGroup = null,
  openOnFocus = false,
  // Optional stable `id` for the input (its list becomes `${id}-list`), so a
  // `<label htmlFor>` can name it and the DOM is the same on every render.
  id = null,
  // 🔢 OPT-IN (2026-10-28, the timesheet Travel (h) cell): `inputMode` sets the phone keypad;
  // `accept(text) → problem | null` marks the box `aria-invalid` and shows the problem under it.
  // It REFUSES nothing itself (the caller decides whether to commit). Both absent = the input
  // renders exactly as before, no attribute added (pinned by SuggestInput.test.js).
  inputMode = null,
  accept = null,
}) {
  const problem = accept ? accept(value) : null;
  const reactId = useId();
  const listId = id ? `${id}-list` : `suggest-${reactId.replace(/:/g, '')}`;
  const [open, setOpen] = useState(false);
  // null = show every option (▾ / ArrowDown); a string = what the user typed.
  const [query, setQuery] = useState(null);
  const [active, setActive] = useState(-1);
  const [panelWidth, setPanelWidth] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const phone = useIsMobileOrderViewport();

  const matches = matchSuggestions(options, query);
  const shown = open && matches.length > 0;

  const close = () => { setOpen(false); setActive(-1); setQuery(null); };
  const { coords, primeCoords } = useAnchoredPortalPanel({
    open: shown, enabled: !phone, align: 'left', triggerRef: wrapRef, panelRef, onDismiss: close,
    // The focused input scrolls its own text (focus, typing) — not a page scroll.
    ignoreScrollWithin: wrapRef,
  });

  const openWith = (q, activeIndex = -1) => {
    if (wrapRef.current) setPanelWidth(wrapRef.current.offsetWidth || null);
    primeCoords();
    setQuery(q);
    setActive(activeIndex);
    setOpen(true);
  };

  // 🔑 No document-level outside-click listener: the INPUT keeps focus for as
  // long as the list is open (▾, the list and its options all preventDefault
  // their mousedown), so any click elsewhere is a BLUR, and blur closes.

  // 📱 Below `md` the list is IN FLOW, so inside a scrolling container (the New
  // gag sheet on a phone) it can open below that container's visible edge —
  // measured at 375: 0 of 5 options reachable until the sheet was scrolled.
  // Bring it into view once, when it opens.
  useEffect(() => {
    if (!shown || !phone) return;
    const el = panelRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [shown, phone]);

  // Keep the highlighted option in view in a scrolling list.
  useEffect(() => {
    if (!shown || active < 0) return;
    const el = document.getElementById(`${listId}-opt-${active}`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [shown, active, listId]);

  const pick = (option) => {
    if (onPick) onPick(option);
    else if (option !== value) onChange(option);
    close();
    inputRef.current?.focus();
  };


  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!shown) { openWith(open ? query : null, 0); return; }
      setActive((i) => (i + 1) % matches.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      if (!shown) return;
      e.preventDefault();
      setActive((i) => (i <= 0 ? matches.length - 1 : i - 1));
      return;
    }
    if (e.key === 'Enter') {
      if (!shown) return; // closed: the surrounding form / row owns Enter
      if (active >= 0 && active < matches.length) {
        e.preventDefault(); // claimed: this Enter is a pick
        pick(matches[active]);
        return;
      }
      // Nothing arrowed onto. Close; with `enterPassesThrough` the key is NOT
      // claimed (no preventDefault), so the surrounding row's Enter-saves runs
      // in the same press — owner 2026-10-19, "ONE ENTER SAVES".
      close();
      if (!enterPassesThrough) e.preventDefault();
      return;
    }
    if (e.key === 'Escape') {
      if (!shown) return;
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Tab' && open) close();
  };

  const onToggle = () => {
    if (disabled) return;
    if (shown) close(); else openWith(null);
    inputRef.current?.focus();
  };

  const activeId = shown && active >= 0 ? `${listId}-opt-${active}` : undefined;

  const list = shown && (
    <ul
      ref={panelRef}
      id={listId}
      role="listbox"
      aria-label={listLabel || ariaLabel}
      data-testid={testId ? `${testId}-list` : undefined}
      data-suggest-list="true"
      // Keep focus in the input (its blur is what closes the list).
      onMouseDown={(e) => e.preventDefault()}
      className={`${LIST_CLASS} ${phone ? 'absolute left-0 right-0 mt-1 z-50' : ''}`}
      style={phone ? undefined : {
        position: 'fixed',
        top: coords ? coords.top : -9999,
        left: coords ? coords.left : -9999,
        zIndex: 60,
        minWidth: panelWidth || undefined,
        maxWidth: 'calc(100vw - 1rem)',
      }}
    >
      {matches.map((o, i) => {
        const current = o === value;
        const group = optionGroup ? optionGroup(o) : null;
        const showGroup = !!group && (i === 0 || optionGroup(matches[i - 1]) !== group);
        return (
          <React.Fragment key={o}>
          {showGroup && (
            <li role="presentation" className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {group}
            </li>
          )}
          <li
            id={`${listId}-opt-${i}`}
            role="option"
            aria-selected={i === active}
            title={o}
            className={`${OPTION_BASE} ${i === active ? OPTION_ACTIVE : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
            onMouseEnter={() => setActive(i)}
            onClick={() => pick(o)}
          >
            <span className="w-3 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden="true">{current ? '✓' : ''}</span>
            <span className={`truncate ${current ? 'font-semibold' : ''}`}>{o}</span>
          </li>
          </React.Fragment>
        );
      })}
    </ul>
  );

  return (
    <div ref={wrapRef} className="relative w-full">
      <input
        ref={inputRef}
        id={id || undefined}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-expanded={shown}
        aria-controls={listId}
        aria-activedescendant={activeId}
        aria-label={ariaLabel}
        autoComplete="off"
        data-testid={testId}
        className={`${inputClassName} pr-8`}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        {...(inputMode ? { inputMode } : {})}
        {...(problem ? { 'aria-invalid': 'true', 'aria-describedby': `${listId}-problem` } : {})}
        onChange={(e) => {
          onChange(e.target.value);
          openWith(e.target.value);
        }}
        onKeyDown={onKeyDown}
        onFocus={openOnFocus ? () => { if (!disabled && !open) openWith(null); } : undefined}
        onBlur={(e) => {
          if (panelRef.current && panelRef.current.contains(e.relatedTarget)) return;
          if (wrapRef.current && wrapRef.current.contains(e.relatedTarget)) return;
          if (open) close();
        }}
      />
      <button
        type="button"
        tabIndex={-1}
        className={TOGGLE_CLASS}
        aria-label={`Show all ${(listLabel || ariaLabel || 'options').toLowerCase()}`}
        title="Show all options"
        data-testid={testId ? `${testId}-toggle` : undefined}
        disabled={disabled}
        // Keep focus in the input, so the list's keyboard keeps working.
        onMouseDown={(e) => e.preventDefault()}
        onClick={onToggle}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 transition-transform duration-150 ${shown ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {problem && (
        <span id={`${listId}-problem`} role="alert" data-testid={testId ? `${testId}-problem` : undefined}
          className={INPUT_PROBLEM_CLASS}>
          {problem}
        </span>
      )}
      {phone ? list : (list && createPortal(list, document.body))}
    </div>
  );
}
